import React, { useState } from 'react'
import { 
  ShoppingCart, Plus, Minus, Check, X, AlertCircle,
  ChefHat, Users, Package
} from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'

interface RecipeShoppingListProps {
  recipeId: string
  recipeName: string
  recipeServings: number
  ingredients: Array<{
    id: string
    ingredient_name: string
    quantity?: number
    unit?: string
    preparation_notes?: string
  }>
  onClose?: () => void
}

// Common pantry items that people usually have
const COMMON_PANTRY_ITEMS = [
  'Salt', 'Pepper', 'Oil', 'Butter', 'Sugar', 'Flour',
  'Garlic', 'Onion', 'Eggs', 'Milk', 'Water', 'Vinegar',
  'Baking Soda', 'Baking Powder', 'Vanilla Extract'
]

export const RecipeShoppingList: React.FC<RecipeShoppingListProps> = ({ 
  recipeId,
  recipeName,
  recipeServings,
  ingredients,
  onClose
}) => {
  const [servings, setServings] = useState(recipeServings || 4)
  const [checkPantry, setCheckPantry] = useState(true)
  const [pantryItems, setPantryItems] = useState<string[]>([])
  const [customPantryItem, setCustomPantryItem] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showPantryCheck, setShowPantryCheck] = useState(false)

  const servingsMultiplier = servings / (recipeServings || 4)

  const handleAddToShoppingList = async () => {
    if (checkPantry && !showPantryCheck) {
      setShowPantryCheck(true)
      return
    }

    setIsSubmitting(true)
    
    try {
      const response = await apiClient.post('/api/shopping/add-recipe-ingredients', {
        recipe_id: recipeId,
        servings_multiplier: servingsMultiplier,
        check_pantry: checkPantry,
        pantry_items: pantryItems
      })

      if (response.success) {
        toast.success(
          <div>
            <strong>{response.recipe_name}</strong>
            <br />
            Added {response.items_added} items to shopping list
            {response.items_skipped > 0 && (
              <><br />Skipped {response.items_skipped} items you already have</>
            )}
          </div>,
          { duration: 5000 }
        )
        
        if (onClose) {
          onClose()
        }
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to add items to shopping list')
    } finally {
      setIsSubmitting(false)
    }
  }

  const togglePantryItem = (itemName: string) => {
    setPantryItems(prev => 
      prev.includes(itemName) 
        ? prev.filter(item => item !== itemName)
        : [...prev, itemName]
    )
  }

  const addCustomPantryItem = () => {
    if (customPantryItem.trim()) {
      setPantryItems(prev => [...prev, customPantryItem.trim()])
      setCustomPantryItem('')
    }
  }

  const adjustedIngredients = ingredients.map(ing => ({
    ...ing,
    adjustedQuantity: ing.quantity ? (ing.quantity * servingsMultiplier).toFixed(2) : undefined
  }))

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center">
            <ShoppingCart className="w-6 h-6 mr-2 text-green-500" />
            Add to Shopping List
          </h2>
          <p className="text-gray-600 mt-1">{recipeName}</p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Servings Adjuster */}
      <div className="mb-6 p-4 bg-blue-50 rounded-lg">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <Users className="inline w-4 h-4 mr-1" />
          Adjust Servings
        </label>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setServings(Math.max(1, servings - 1))}
            className="p-2 bg-white rounded-lg border hover:bg-gray-50"
          >
            <Minus className="w-4 h-4" />
          </button>
          <div className="text-center">
            <div className="text-2xl font-bold">{servings}</div>
            <div className="text-xs text-gray-500">servings</div>
          </div>
          <button
            onClick={() => setServings(servings + 1)}
            className="p-2 bg-white rounded-lg border hover:bg-gray-50"
          >
            <Plus className="w-4 h-4" />
          </button>
          
          {servings !== recipeServings && (
            <div className="ml-4 text-sm text-gray-600">
              (Original: {recipeServings} servings)
            </div>
          )}
        </div>
      </div>

      {/* Check Pantry Option */}
      <div className="mb-6">
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={checkPantry}
            onChange={(e) => {
              setCheckPantry(e.target.checked)
              setShowPantryCheck(false)
            }}
            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700">
            Check pantry first - only add items I don't have
          </span>
        </label>
      </div>

      {/* Pantry Check List */}
      {showPantryCheck && checkPantry && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
            <Package className="w-4 h-4 mr-2" />
            Which items do you already have?
          </h3>
          
          {/* Common Pantry Items */}
          <div className="mb-3">
            <p className="text-xs text-gray-600 mb-2">Common pantry items:</p>
            <div className="flex flex-wrap gap-2">
              {COMMON_PANTRY_ITEMS.map(item => {
                const hasItem = pantryItems.includes(item)
                const isInRecipe = adjustedIngredients.some(ing => 
                  ing.ingredient_name.toLowerCase().includes(item.toLowerCase())
                )
                
                if (!isInRecipe) return null
                
                return (
                  <button
                    key={item}
                    onClick={() => togglePantryItem(item)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      hasItem
                        ? 'bg-green-100 text-green-800 border border-green-300'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {hasItem && <Check className="inline w-3 h-3 mr-1" />}
                    {item}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Recipe Ingredients Checklist */}
          <div className="space-y-2 mb-3">
            <p className="text-xs text-gray-600 mb-2">Recipe ingredients:</p>
            {adjustedIngredients.map(ing => {
              const hasItem = pantryItems.includes(ing.ingredient_name)
              
              return (
                <label
                  key={ing.id}
                  className="flex items-center space-x-2 cursor-pointer hover:bg-white p-2 rounded"
                >
                  <input
                    type="checkbox"
                    checked={hasItem}
                    onChange={() => togglePantryItem(ing.ingredient_name)}
                    className="w-4 h-4 text-green-600 rounded"
                  />
                  <span className={`text-sm ${hasItem ? 'line-through text-gray-400' : ''}`}>
                    {ing.adjustedQuantity && `${ing.adjustedQuantity} `}
                    {ing.unit && `${ing.unit} `}
                    {ing.ingredient_name}
                  </span>
                </label>
              )
            })}
          </div>

          {/* Custom Item Add */}
          <div className="flex space-x-2">
            <input
              type="text"
              value={customPantryItem}
              onChange={(e) => setCustomPantryItem(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addCustomPantryItem()}
              placeholder="Add other items you have..."
              className="flex-1 px-3 py-2 border rounded-lg text-sm"
            />
            <button
              onClick={addCustomPantryItem}
              className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm"
            >
              Add
            </button>
          </div>

          {pantryItems.length > 0 && (
            <div className="mt-3 p-2 bg-green-50 rounded text-sm text-green-700">
              <AlertCircle className="inline w-4 h-4 mr-1" />
              {pantryItems.length} items will be skipped
            </div>
          )}
        </div>
      )}

      {/* Ingredients Preview */}
      {!showPantryCheck && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
            <ChefHat className="w-4 h-4 mr-2" />
            Items to Add ({adjustedIngredients.filter(ing => !pantryItems.includes(ing.ingredient_name)).length})
          </h3>
          <div className="space-y-1 max-h-60 overflow-y-auto">
            {adjustedIngredients.map(ing => {
              const isSkipped = checkPantry && pantryItems.includes(ing.ingredient_name)
              
              return (
                <div
                  key={ing.id}
                  className={`text-sm py-1 ${isSkipped ? 'line-through text-gray-400' : 'text-gray-700'}`}
                >
                  {ing.adjustedQuantity && <span className="font-medium">{ing.adjustedQuantity}</span>}
                  {ing.unit && ` ${ing.unit}`}
                  {` ${ing.ingredient_name}`}
                  {isSkipped && <span className="ml-2 text-xs">(have)</span>}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex space-x-3">
        <button
          onClick={handleAddToShoppingList}
          disabled={isSubmitting}
          className="flex-1 px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Adding...</span>
            </>
          ) : showPantryCheck && checkPantry ? (
            <>
              <ShoppingCart className="w-5 h-5" />
              <span>Continue to Add Items</span>
            </>
          ) : (
            <>
              <ShoppingCart className="w-5 h-5" />
              <span>Add to Shopping List</span>
            </>
          )}
        </button>
        
        {onClose && (
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  )
}